import Head from 'next/head';
import NextLink from 'next/link';
import React from 'react';
import { ArrowDownward } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { Box, Container, Typography, Grid, Link } from '@mui/material';
import BaseLayout from 'layouts/BaseLayout';
import EmbedVideo from 'components/EmbedVideo';
import List from 'components/Templates/List';
import Header from 'components/Templates/Header';
import FriendsReferralBanner from 'components/Invite/FriendsReferralBanner';
import FormatsService from 'services/FormatsService';
import TemplatesService from 'services/TemplatesService';
import Faq from 'components/Templates/Faq';
import useIsMobile from 'hooks/useIsMobile';
import { arrayMoveImmutable } from 'array-move';
import CustomButton from 'components/CustomButton';
import { COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import Breadcrumb from 'components/Templates/Breadcrumb';
import { useRouter } from 'next/router';

export const useStyles = makeStyles((theme) => ({
  primaryColor: {
    color: '#7833FF',
  },
  sectionImage: {
    display: 'block',
    maxWidth: '100%',
    margin: '0 auto',
  },
  sectionBanners: {
    [theme.breakpoints.down('md')]: {
      display: 'grid',
      justifyItems: 'center',
      textAlign: 'center',
    },
  },
}));

function getFormatListData(slug) {
  const data = {
    slides: {
      columns: 5,
      perPage: 15,
    },
    abstracts: {
      columns: 3,
      perPage: 9,
    },
    infographics: {
      columns: 6,
      perPage: 18,
    },
    posters: {
      columns: 5,
      perPage: 15,
    },
  };

  if (data[slug]) {
    return data[slug];
  }

  throw new Error(`Unable to get format list data for unknown format [${slug}].`);
}

function orderFormats(formats) {
  const abstractsIndex = formats.findIndex((format) => format.slug === 'abstracts');

  return arrayMoveImmutable(formats, abstractsIndex, 0);
}

export async function getStaticProps() {
  try {
    const { data: formats } = await FormatsService.fetchAll();

    for (const format of formats) {
      let perPage;

      switch (format.slug) {
        case 'slides': {
          perPage = 5;
          break;
        }
        case 'abstracts': {
          perPage = 3;
          break;
        }
        case 'infographics': {
          perPage = 6;
          break;
        }
        case 'posters': {
          perPage = 5;
          break;
        }
        default: {
          throw new Error(
            `Unable to determine "perPage" value for unknown format [${format.slug}].`,
          );
        }
      }

      const { data: templates } = await TemplatesService.fetchAll({ formatId: format.id, perPage });

      format.templates = templates;
    }

    return {
      props: {
        formats: orderFormats(formats),
      },
      revalidate: 604800, // 1 week
    };
  } catch (e) {
    console.error(e);

    return {
      props: { formats: [] },
      revalidate: 604800, // 1 week
    };
  }
}

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What are infographic templates?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Infographic templates are ready-to-edit infographics created by our design team. You can use them as inspiration or start-point for your creations.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where can I get free infographic templates?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can get free infographic templates here. in our infographic template tab.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I make an infographic template?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'To make an infographic template you’ll need an account at Mind the Graph and some creativity to start creating. Click here and learn simple steps to create an eye-catching infographic.',
      },
    },
    {
      '@type': 'Question',
      name: 'What are the 7 types of infographics?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The 7 types of infographics are: statistical, timelines, processes, comparisons, lists, geographic, and informational. You can learn more about the difference between infographics and graphical abstract.',
      },
    },
  ],
};

export default function TemplatesPage({ formats }) {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const router = useRouter();

  return (
    <BaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
    >
      <Head>
        <title>Free infographic templates made by scientists | Mind the Graph</title>

        <meta
          name="description"
          content="Over 40,000 options of infographic template to to customize your science poster, enhance your graphical abstracts and boost your research. Try it for free!"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>

      <Header>
        <Box mt={4}>
          <Typography variant="h6" component="h2" align="center" gutterBottom={true}>
            <b>... or, explore all our ready-to-go templates</b>
          </Typography>

          <Typography align="center">
            <ArrowDownward />
          </Typography>
        </Box>
      </Header>

      <Box py={6} bgcolor="common.white">
        <Container>
          <Box mb={6}>
            <Breadcrumb items={[{ label: 'Infographic Templates', path: router.asPath }]} />
          </Box>

          <h1>Infographic Templates</h1>

          <p>
            Browse our Infographic Templates created by scientists for scientists. Customize your
            perfect Infographic Templates in minutes, then download and share your impactful
            research visualization instantly.
          </p>

          {formats.map((format, index) => (
            <Box key={index} mb={index !== formats.length - 1 ? 4 : 0}>
              <List
                key={index}
                breadcrumbItems={[
                  {
                    label: format.name,
                    path: `/infographic-templates/format/${format.slug}`,
                  },
                ]}
                formatName={format.name}
                templates={format.templates}
                columns={getFormatListData(format.slug).columns}
                expandButtonUrl={`/infographic-templates/format/${format.slug}`}
              />
            </Box>
          ))}
        </Container>
      </Box>

      <Box py={6} bgcolor="grey.200">
        <Container>
          <Grid container={true} spacing={2} alignItems="center">
            {!isMobile && (
              <Grid item={true} xs={12} md={6}>
                <NextLink
                  href="/infographic-templates/category/neutral"
                  prefetch={false}
                  passHref={true}
                >
                  <Link underline="hover">
                    <img
                      className={classes.sectionImage}
                      src="/images/neutral-infographic-templates.svg"
                      alt="Neutral infographic templates"
                    />
                  </Link>
                </NextLink>
              </Grid>
            )}

            <Grid item={true} xs={12} md={6}>
              <Box className={classes.sectionBanners} px={6}>
                <img src="/images/creation-icon.svg" alt="Creation icon" />

                <Typography
                  className={classes.primaryColor}
                  variant={isMobile ? 'body1' : 'h5'}
                  component="h2"
                  gutterBottom={true}
                >
                  <b>Need a more neutral infographic template?</b>
                </Typography>

                <Typography>
                  If you can't find an ideal theme for your template, try using one without a theme
                  and let your imagination create your dream template.
                </Typography>

                {isMobile && (
                  <Grid item={true} xs={12} md={6}>
                    <NextLink
                      href="/infographic-templates/category/neutral"
                      prefetch={false}
                      passHref={true}
                    >
                      <Link underline="hover">
                        <img
                          className={classes.sectionImage}
                          src="/images/neutral-infographic-templates.svg"
                          alt="Neutral infographic templates"
                        />
                      </Link>
                    </NextLink>
                  </Grid>
                )}

                <Box mt={4}>
                  <NextLink
                    href="/infographic-templates/category/neutral"
                    prefetch={false}
                    passHref={true}
                  >
                    <CustomButton>See neutral templates</CustomButton>
                  </NextLink>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box pt={6} pb={18} bgcolor="common.white">
        <Container>
          <Grid container={true} spacing={2} alignItems="center">
            <Grid item={true} xs={12} md={6}>
              <Box px={6} className={classes.sectionBanners}>
                <img src="/images/infographic-idea-icon.png" alt="Infographic idea icon" />

                <Typography
                  className={classes.primaryColor}
                  variant={isMobile ? 'body1' : 'h5'}
                  component="h2"
                  gutterBottom={true}
                >
                  <b>Running out of ideas for an infographic?</b>
                </Typography>

                <Box mb={2}>
                  <Typography>
                    An infographic should be self-explanatory. So, don't forget that making one
                    means trying to communicate and it shall be simple for the reader.
                  </Typography>
                </Box>

                <Typography>
                  Our infographic templates have several sizes, designs, and color palettes. Check
                  out our gallery and select the best one for you to show your work!
                </Typography>
              </Box>
            </Grid>

            <Grid item={true} xs={12} md={6}>
              <NextLink href="/my-creations" passHref={true}>
                <Link underline="hover">
                  <img
                    className={classes.sectionImage}
                    src="/images/infographic-ideas-templates.png"
                    alt="Infographic ideas templates"
                  />
                </Link>
              </NextLink>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box py={6} bgcolor="grey.200">
        <Container>
          <Box mt={-15} mb={6}>
            <FriendsReferralBanner />
          </Box>

          <Grid container={true} spacing={2} alignItems="center">
            {!isMobile && (
              <Grid item={true} xs={12} md={6}>
                <section aria-label="Video">
                  <EmbedVideo url="https://www.youtube.com/embed/0x_2IzqCHGg" />
                </section>
              </Grid>
            )}

            <Grid item={true} xs={12} md={6}>
              <Box px={6} className={classes.sectionBanners}>
                <img src="/images/video-play-icon.png" alt="Video play icon" />

                <Typography
                  className={classes.primaryColor}
                  variant={isMobile ? 'body1' : 'h5'}
                  component="h2"
                  gutterBottom={true}
                >
                  <b>Want some tips on how to edit your infographic template?</b>
                </Typography>

                <Box pb={isMobile ? 3 : 0}>
                  <Typography>
                    Mind The Graph cares to help you get exactly where you want to go. If you need a
                    hand, check our Youtube channel. There, you can check our expert's tips on how
                    to create and edit your infographic template to reach your goals.
                  </Typography>
                </Box>

                {isMobile && (
                  <Grid container={true} spacing={3} alignItems="center" mt={3}>
                    <Grid item={true} xs={12}>
                      <section aria-label="Video">
                        <EmbedVideo url="https://www.youtube.com/embed/0x_2IzqCHGg" />
                      </section>
                    </Grid>
                  </Grid>
                )}

                <Box mt={4}>
                  <CustomButton
                    href="https://www.youtube.com/c/MindtheGraph/videos"
                    target="_blank"
                  >
                    See video tips
                  </CustomButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box py={6} bgcolor="common.white">
        <Container>
          <Faq />
        </Container>
      </Box>
    </BaseLayout>
  );
}
